import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

export interface PageHeroProps {
  /** Legacy image prop retained while public pages migrate to shared light artwork. */
  heroImageUrl?: string;
  badge?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  className?: string;
  overlayClassName?: string;
}

const previewForPath = (path: string) => {
  if (path.startsWith('/foundation'))
    return [
      'foundation-preview.png',
      'Illustrative vision statement in the strategic foundation workspace',
    ];
  if (path.startsWith('/external-assessment') || path.startsWith('/internal-assessment'))
    return [
      'assessment-preview.png',
      'Illustrative strengths, weaknesses, opportunities and threats assessment',
    ];
  if (path.startsWith('/strategy-formulation'))
    return [
      'priorities-preview.png',
      'Illustrative strategic priorities in a guided planning workflow',
    ];
  if (path.startsWith('/execution-monitoring'))
    return ['execution-preview.png', 'Illustrative projects and tasks connected to a strategy'];
  if (path === '/team-collaboration')
    return [
      'people-preview.png',
      'Illustrative organization chart connecting people and ownership',
    ];
  if (path === '/performance-tracking')
    return ['report-preview.png', 'Illustrative strategic report with PDF export'];
  if (path === '/strategic-framework' || path === '/ai-insights')
    return [
      'strategy-workspace.png',
      'Illustrative connected strategy workspace with company context and AI assistance',
    ];
  return null;
};

export default function PageHero({
  badge,
  title,
  subtitle,
  children,
  className = '',
}: PageHeroProps) {
  const { pathname } = useLocation();
  const preview = previewForPath(pathname);
  const words = typeof title === 'string' ? title.split(' ') : [];
  const displayTitle =
    words.length > 5 ? (
      <>
        {words.slice(0, -3).join(' ')}{' '}
        <span className="public-hero-accent">{words.slice(-3).join(' ')}</span>
      </>
    ) : (
      title
    );
  return (
    <section className={`public-hero ${preview ? 'public-hero--illustrated' : ''} ${className}`}>
      <div className="public-container public-hero-grid">
        <div className="public-hero-copy">
          {badge ? (
            <div className="public-hero-badge">{badge}</div>
          ) : (
            <p className="public-eyebrow">Strategy, connected.</p>
          )}
          <h1>{displayTitle}</h1>
          {subtitle && <p className="public-hero-description">{subtitle}</p>}
          {children && <div className="public-hero-actions">{children}</div>}
        </div>
        {preview && (
          <figure className="public-hero-preview">
            <img src={`/images/homepage/${preview[0]}`} alt={preview[1]} fetchPriority="high" />
            <figcaption className="sr-only">Illustrative product view.</figcaption>
          </figure>
        )}
      </div>
    </section>
  );
}
